import { TabActive } from "../../../../types";
import {
  Budget,
  BudgetItemTypeEnum,
  CategoryTotals,
  HeaderOptions,
  keysCategoryTotalsBRL,
  Totals,
  versionInfo,
} from "../../../../types/budget/budget";
import { filterVisibleCategoryTotals } from "../../../../utils/visible-stage-totals";

const ZERO_TOTALS: Totals = { totalInBRL: 0, totalInCurrency: 0 };

export function emptyCategoryTotals(): CategoryTotals {
  return keysCategoryTotalsBRL.reduce((acc, key) => {
    acc[key] = { ...ZERO_TOTALS };
    return acc;
  }, {} as CategoryTotals);
}

function copyTotalsLeaf(value: Totals | undefined): Totals {
  return { ...(value ?? ZERO_TOTALS) };
}

function sumCategoryKeyForSupplier(
  categoryTotals: any[] | undefined,
  supplierId: string,
  key: string,
  type: string,
  calcOption: string,
  version: number,
  stageStatus: string,
  budget: Budget
): Totals {
  const supplierTotals = filterVisibleCategoryTotals(
    categoryTotals,
    type,
    calcOption,
    version,
    {
      supplierList: budget.supplierList,
      budgetServices: budget.budgetServices,
      addedItemTypeList: budget.addedItemTypeList,
      status: stageStatus,
    },
    supplierId
  );

  if (supplierTotals.length === 0) {
    return { ...ZERO_TOTALS };
  }

  return supplierTotals.reduce(
    (acc, curr) => {
      const currValue = curr?.[key];
      if (!currValue) return acc;
      if (typeof currValue === "object" && "totalInBRL" in currValue) {
        return {
          totalInBRL: (acc.totalInBRL || 0) + (currValue.totalInBRL || 0),
          totalInCurrency:
            (acc.totalInCurrency || 0) + (currValue.totalInCurrency || 0),
        };
      }
      return acc;
    },
    { totalInBRL: 0, totalInCurrency: 0 }
  );
}

export function defineTotals(
  activeCalcOption: HeaderOptions,
  detailedBudget: string,
  tabActive: number,
  activeBudget: Budget,
  supplierId: string,
  type: keyof typeof BudgetItemTypeEnum,
  versionInfo: versionInfo
) {
  const splitDetailed = detailedBudget.split("-");
  const version = Number(splitDetailed[2]);
  const visibilityVersion = Number.isFinite(versionInfo?.version)
    ? versionInfo.version
    : version;
  const calcTotalsSource = emptyCategoryTotals();
  const calcTotalsCompare = emptyCategoryTotals();

  // O status é o da árvore lida (orçamento / negociação / fechamento): é ele
  // que decide em que forma de cobrança cai uma categoria movida na etapa.
  const fillFrom = (
    target: CategoryTotals,
    categoryTotals: any[] | undefined,
    stageVersion: number,
    stageStatus: string
  ) => {
    if (!Array.isArray(categoryTotals) || categoryTotals.length === 0) {
      for (const key of keysCategoryTotalsBRL) {
        target[key] = copyTotalsLeaf(undefined);
      }
      return;
    }
    for (const key of keysCategoryTotalsBRL) {
      target[key] = copyTotalsLeaf(
        sumCategoryKeyForSupplier(
          categoryTotals,
          supplierId,
          key,
          type,
          activeCalcOption,
          stageVersion,
          stageStatus,
          activeBudget
        )
      );
    }
  };

  if (activeCalcOption !== "budgetTotals") {
    switch (tabActive) {
      case TabActive.NEGOTIATION:
        fillFrom(
          calcTotalsSource,
          activeBudget?.negociationValuesTotal[version - 1]
            ?.byCalculationType?.[activeCalcOption][type],
          visibilityVersion,
          versionInfo.status
        );
        if (versionInfo.comparisonVersion > 0) {
          fillFrom(
            calcTotalsCompare,
            activeBudget?.negociationValuesTotal[
              versionInfo.comparisonVersion - 1
            ]?.byCalculationType?.[activeCalcOption][type],
            versionInfo.comparisonVersion,
            "NEGOTIATE"
          );
        } else {
          fillFrom(
            calcTotalsCompare,
            activeBudget?.budgetValuesTotal?.byCalculationType?.[
              activeCalcOption
            ][type],
            0,
            "OPEN"
          );
        }
        return { calcTotalsSource, calcTotalsCompare };
      case TabActive.APPROVED:
        fillFrom(
          calcTotalsSource,
          activeBudget?.closingValuesTotal?.byCalculationType?.[
            activeCalcOption
          ][type],
          visibilityVersion,
          "APPROVED"
        );
        if (versionInfo.comparisonVersion > 0) {
          fillFrom(
            calcTotalsCompare,
            activeBudget?.negociationValuesTotal[
              versionInfo.comparisonVersion - 1
            ]?.byCalculationType?.[activeCalcOption][type],
            versionInfo.comparisonVersion,
            "NEGOTIATE"
          );
        } else {
          fillFrom(
            calcTotalsCompare,
            activeBudget?.budgetValuesTotal?.byCalculationType?.[
              activeCalcOption
            ][type],
            0,
            "OPEN"
          );
        }
        return { calcTotalsSource, calcTotalsCompare };
      case TabActive.BUDGET:
      default:
        fillFrom(
          calcTotalsSource,
          activeBudget?.budgetValuesTotal?.byCalculationType?.[
            activeCalcOption
          ][type],
          visibilityVersion,
          "OPEN"
        );
        return { calcTotalsSource, calcTotalsCompare };
    }
  }

  return { calcTotalsSource, calcTotalsCompare };
}
